import React, { createContext, useContext, useState, useCallback } from 'react';

export interface CartProduct {
  id: string;
  name: string;
  brand: string;
  category: string;
  price: number;
  originalPrice: number;
  discount: number;
  image: string;
  images: string[];
  rating: number;
  reviews: number;
  description: string;
  specs: Record<string, string>;
  inStock: boolean;
  isFlashSale?: boolean;
  isFeatured?: boolean;
  selectedColor?: string;
  selectedStorage?: string;
}

export interface CartItem {
  product: CartProduct;
  quantity: number;
  selectedColor?: string;
  selectedStorage?: string;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (product: CartProduct) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const addToCart = useCallback((product: CartProduct) => {
    if (!product || !product.id) {
      console.error('Invalid product passed to addToCart');
      return;
    }
    
    const key = `${product.id}__${product.selectedColor ?? ''}__${product.selectedStorage ?? ''}`;
    setItems(prev => {
      const existing = prev.find(item => 
        `${item?.product?.id ?? ''}__${item?.product?.selectedColor ?? ''}__${item?.product?.selectedStorage ?? ''}` === key
      );
      if (existing) {
        return prev.map(item => 
          `${item?.product?.id ?? ''}__${item?.product?.selectedColor ?? ''}__${item?.product?.selectedStorage ?? ''}` === key 
            ? { ...item, quantity: item.quantity + 1 } 
            : item
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
    setIsOpen(true);
  }, []);

  const removeFromCart = useCallback((productId: string) => {
    if (!productId) return;
    setItems(prev => prev.filter(item => item?.product?.id !== productId));
  }, []);

  const updateQuantity = useCallback((productId: string, quantity: number) => {
    if (!productId) return;
    if (quantity <= 0) setItems(prev => prev.filter(item => item?.product?.id !== productId));
    else setItems(prev => prev.map(item => item?.product?.id === productId ? { ...item, quantity } : item));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);
  const totalItems = items?.reduce((sum, item) => sum + (item?.quantity ?? 0), 0) ?? 0;
  const totalPrice = items?.reduce((sum, item) => sum + ((item?.product?.price ?? 0) * (item?.quantity ?? 0)), 0) ?? 0;

  return (
    <CartContext.Provider value={{ items, addToCart, removeFromCart, updateQuantity, clearCart, totalItems, totalPrice, isOpen, setIsOpen }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};
